import { z } from "zod";
import { defineEndpoint } from "../endpoint.js";
import { appVersionSchema } from "./health.js";
/**
 * POS updates (POS-11, PRD §17.4, docs/specs/pos-sync.md "Built in B-53"). CI publishes each release to object
 * storage (R2) as a channel manifest; the API offers it to devices through Tauri's dynamic update format, with a
 * staged rollout, and enforces the minimum versions in the version policy. Devices install only when no shift is open.
 */
export const UPDATE_CHANNELS = ["stable", "beta"];
export const updateChannelSchema = z.enum(UPDATE_CHANNELS);
/** `<target>-<arch>` as Tauri names them (`windows-x86_64`, `darwin-aarch64`, `linux-x86_64`). */
export const UPDATE_TARGETS = ["windows", "darwin", "linux"];
export const UPDATE_ARCHES = ["x86_64", "aarch64", "i686", "armv7"];
/** `platform/pos/channels/<channel>.json`, written by the release workflow. */
export const posReleaseManifestSchema = z.object({
    version: appVersionSchema,
    notes: z.string().max(4_000),
    pubDate: z.iso.datetime(),
    /** Share of devices (by a hash of the device id) offered this release; devices below the minimum always are. */
    rolloutPercent: z.int().min(0).max(100),
    platforms: z.record(z.string().regex(/^[a-z]+-[a-z0-9_]+$/), z.object({ url: z.url({ protocol: /^https?$/ }), signature: z.string().min(1) })),
});
/** `platform/pos/policy.json`: the minimum versions every device must run (null: none). */
export const posVersionPolicySchema = z.object({
    /** Below it the POS must update before selling again (it finishes an open shift first). */
    minVersion: appVersionSchema.nullable(),
    /** Below it the server refuses pushes with `APP_UPGRADE_REQUIRED` (426); bills wait on the device. */
    minPushVersion: appVersionSchema.nullable(),
});
export const posUpdateParamsSchema = z.object({
    target: z.enum(UPDATE_TARGETS),
    arch: z.enum(UPDATE_ARCHES),
    currentVersion: appVersionSchema,
});
export const posUpdateQuerySchema = z.object({ channel: updateChannelSchema.default("stable") });
/**
 * Tauri's dynamic update response. When nothing is offered the device's own version comes back (Tauri installs only
 * a newer version), with a placeholder `url` that is never fetched.
 */
export const posUpdateSchema = z.object({
    version: appVersionSchema,
    notes: z.string(),
    pub_date: z.iso.datetime().nullable(),
    url: z.url(),
    signature: z.string(),
});
export const getPosUpdate = defineEndpoint({
    tag: "pos-sync",
    name: "getPosUpdate",
    method: "GET",
    path: "/v1/pos/updates/{target}/{arch}/{currentVersion}",
    access: { device: true },
    summary: "The release this device should install (Tauri updater format); its own version when none is offered",
    params: posUpdateParamsSchema,
    query: posUpdateQuerySchema,
    status: 200,
    response: posUpdateSchema,
});
//# sourceMappingURL=updates.js.map